/**
 * 索引更新操作类型
 */
export var IndexUpdateType;
(function (IndexUpdateType) {
    IndexUpdateType["ADD_ENTITY"] = "add_entity";
    IndexUpdateType["REMOVE_ENTITY"] = "remove_entity";
    IndexUpdateType["UPDATE_ENTITY"] = "update_entity";
})(IndexUpdateType || (IndexUpdateType = {}));
/**
 * 延迟索引更新器，用于批量更新查询索引以提高性能
 */
export class IndexUpdateBatcher {
    constructor() {
        this.pendingOperations = [];
        this.isProcessing = false;
        this.batchSize = 1000;
        this.flushTimeout = null;
        this.flushDelay = 16; // 16ms，约60fps
    }
    /**
     * 添加索引更新操作
     */
    addOperation(operation) {
        this.pendingOperations.push(operation);
        // 如果达到批量大小，立即处理
        if (this.pendingOperations.length >= this.batchSize) {
            this.flush();
        }
        else {
            // 否则延迟处理
            this.scheduleFlush();
        }
    }
    /**
     * 批量添加实体
     */
    addEntities(entities) {
        for (const entity of entities) {
            this.pendingOperations.push({
                type: IndexUpdateType.ADD_ENTITY,
                entity
            });
        }
        if (this.pendingOperations.length >= this.batchSize) {
            this.flush();
        }
        else {
            this.scheduleFlush();
        }
    }
    /**
     * 批量移除实体
     */
    removeEntities(entities) {
        for (const entity of entities) {
            this.pendingOperations.push({
                type: IndexUpdateType.REMOVE_ENTITY,
                entity
            });
        }
        if (this.pendingOperations.length >= this.batchSize) {
            this.flush();
        }
        else {
            this.scheduleFlush();
        }
    }
    /**
     * 批量更新实体
     */
    updateEntities(updates) {
        for (const update of updates) {
            this.pendingOperations.push({
                type: IndexUpdateType.UPDATE_ENTITY,
                entity: update.entity,
                oldMask: update.oldMask,
                newMask: update.newMask
            });
        }
        if (this.pendingOperations.length >= this.batchSize) {
            this.flush();
        }
        else {
            this.scheduleFlush();
        }
    }
    /**
     * 安排延迟刷新
     */
    scheduleFlush() {
        if (this.flushTimeout) {
            return;
        }
        this.flushTimeout = setTimeout(() => {
            this.flush();
        }, this.flushDelay);
    }
    /**
     * 立即处理所有待处理的操作
     */
    flush() {
        if (this.isProcessing || this.pendingOperations.length === 0) {
            return;
        }
        this.isProcessing = true;
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }
        try {
            this.processBatch();
        }
        finally {
            this.isProcessing = false;
        }
    }
    /**
     * 处理批量操作
     */
    processBatch() {
        const operations = this.pendingOperations;
        this.pendingOperations = [];
        // 按操作类型分组以优化处理
        const addOperations = [];
        const removeOperations = [];
        const updateOperations = [];
        for (const operation of operations) {
            switch (operation.type) {
                case IndexUpdateType.ADD_ENTITY:
                    addOperations.push(operation.entity);
                    break;
                case IndexUpdateType.REMOVE_ENTITY:
                    removeOperations.push(operation.entity);
                    break;
                case IndexUpdateType.UPDATE_ENTITY:
                    if (operation.oldMask !== undefined && operation.newMask !== undefined) {
                        updateOperations.push({
                            entity: operation.entity,
                            oldMask: operation.oldMask,
                            newMask: operation.newMask
                        });
                    }
                    break;
            }
        }
        // 批量处理每种类型的操作
        if (addOperations.length > 0) {
            this.processBatchAdd(addOperations);
        }
        if (removeOperations.length > 0) {
            this.processBatchRemove(removeOperations);
        }
        if (updateOperations.length > 0) {
            this.processBatchUpdate(updateOperations);
        }
    }
    /**
     * 批量处理添加操作
     */
    processBatchAdd(entities) {
        // 这里应该调用QuerySystem的批量添加方法
        // 由于需要访问QuerySystem，这个方法应该由外部注入处理函数
        if (this.onBatchAdd) {
            this.onBatchAdd(entities);
        }
    }
    /**
     * 批量处理移除操作
     */
    processBatchRemove(entities) {
        if (this.onBatchRemove) {
            this.onBatchRemove(entities);
        }
    }
    /**
     * 批量处理更新操作
     */
    processBatchUpdate(updates) {
        if (this.onBatchUpdate) {
            this.onBatchUpdate(updates);
        }
    }
    /**
     * 设置批量大小
     */
    setBatchSize(size) {
        this.batchSize = Math.max(1, size);
    }
    /**
     * 设置刷新延迟
     */
    setFlushDelay(delay) {
        this.flushDelay = Math.max(0, delay);
    }
    /**
     * 获取待处理操作数量
     */
    getPendingCount() {
        return this.pendingOperations.length;
    }
    /**
     * 清空所有待处理操作
     */
    clear() {
        this.pendingOperations.length = 0;
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }
    }
    /**
     * 检查是否有待处理操作
     */
    hasPendingOperations() {
        return this.pendingOperations.length > 0;
    }
}
//# sourceMappingURL=IndexUpdateBatcher.js.map